import * as Sentry from "@sentry/react";
import store from "../store/index";
import { shouldIgnoreSpan } from "./sentrySpanConfig";

/**
 * Initialize Sentry SDK - called early in app lifecycle
 * Environment-based configuration:
 * - Development: Full logging, all traces, all errors
 * - Production: Focus on production-level issues only, error-only replay
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY;
  
  // Get environment from Vite (development/production)
  const environment = import.meta.env.MODE || "development";
  const isDevelopment = environment === "development";
  const isProduction = environment === "production";
  
  // Get API base URL from environment
  const apiBaseUrl = import.meta.env.VITE_API_V2_URL || "";
  
  // Environment-based configuration
  const config = {
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || "app-dashboard@0.1.0",
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          apiBaseUrl,
          /^https?:\/\/localhost/,
          /^\/v2\//,
          /^\/api\//,
        ],
        // Exclude live orders endpoint from automatic tracing
        shouldCreateSpanForRequest(url) {
          // Don't trace the live orders polling endpoint
          if (url.includes("/v2/orders/live")) {
            return false;
          }
          return true;
        },
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance Monitoring - Lower rate in production
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
    
    // Session Replay - Only capture errors (no regular session replays)
    replaysSessionSampleRate: 0, // 0% - Don't capture regular sessions
    replaysOnErrorSampleRate: 1.0, // 100% - Capture all sessions with errors
    
    // Logging - Only enable in development
    enableLogs: isDevelopment,
    
    // Before send hook - Filter events based on environment
    beforeSend(event, hint) {
      // Add custom tags
      event.tags = {
        ...event.tags,
        platform: "web",
        client: "browser",
        version: "0.1.0",
      };
      
      // In production, focus on production-level issues only
      if (isProduction) {
        // Filter out development-level logs and info messages
        if (event.level === "info" || event.level === "debug" || event.level === "log") {
          // Only allow error, warning, and fatal levels in production
          return null;
        }
        
        // Filter out console.log messages in production
        if (event.logger === "console" && event.level !== "error" && event.level !== "warning") {
          return null;
        }
      }
      
      // In development, capture everything
      return event;
    },
    
    // Filter logs - Only capture error-level logs in production
    beforeSendLog(log) {
      if (isProduction) {
        // Only send error and warning logs in production
        if (log.level !== "error" && log.level !== "warning" && log.level !== "fatal") {
          return null;
        }
      }
      return log;
    },
    
    // Filter spans - Exclude high-frequency operations
    // Configuration is managed in sentrySpanConfig.js
    // Request bodies are added directly in span attributes when creating POST/PUT/PATCH spans
    beforeSendSpan(span) {
      if (shouldIgnoreSpan(span)) {
        return null;
      }
      return span;
    },
  };
  
  Sentry.init(config);
};

/**
 * Update user context from restaurantInfo
 * Call this whenever restaurantInfo is updated
 * 
 * Based on actual store structure:
 * - restaurantInfo.id (UUID: "5ecd9fe3-3bc3-4724-acb6-27f1adf33ba5")
 * - restaurantInfo.name (restaurant name: "QRapid")
 * - restaurantInfo.email (restaurant email: "test@qrapid.io")
 * - restaurantInfo.contact (phone numbers: "9004318447,9137997312")
 * - restaurantInfo.address (address: "Worli , Mumbai")
 * - restaurantInfo.description (plan info: "P|ACTIVE|2025-10-05|2042-10-07|6000")
 * - restaurantInfo.partnerDetails (array of partner objects)
 * - restaurantInfo.config.printers (array of printer configs)
 */
export const updateUserContext = () => {
  if (store.restaurantInfo) {
    // Map fields based on actual store structure
    const userId = store.restaurantInfo.id?.toString() || null;
    const email = store.restaurantInfo.email || null;
    const username = store.restaurantInfo.name || null;
    const contact = store.restaurantInfo.contact || null;
    const address = store.restaurantInfo.address || null;
    const status = store.restaurantInfo.status || null;
    
    // Set user context for Sentry
    Sentry.setUser({
      id: userId,
      email: email,
      username: username,
    });
    
    // Set additional restaurant context
    Sentry.setContext("restaurant", {
      id: userId,
      email: email,
      name: username,
      contact: contact,
      address: address,
      status: status,
      logo: store.restaurantInfo.logo,
      description: store.restaurantInfo.description, // Contains plan info: "P|ACTIVE|2025-10-05|2042-10-07|6000"
      latitude: store.restaurantInfo.latitude,
      longitude: store.restaurantInfo.longitude,
      timingFrom: store.restaurantInfo.timingFrom,
      timingTo: store.restaurantInfo.timingTo,
      createdAt: store.restaurantInfo.createdAt,
      updatedAt: store.restaurantInfo.updatedAt,
      // Partner details
      partnerCount: store.restaurantInfo.partnerDetails?.length || 0,
      enabledPartners: store.restaurantInfo.partnerDetails
        ?.filter(p => p.enabled)
        .map(p => p.partnerName)
        .join(", ") || "none",
      // Printer config
      printerCount: store.restaurantInfo.config?.printers?.length || 0,
    });
    
    // Add breadcrumb for context update
    Sentry.addBreadcrumb({
      category: "sentry.user_context",
      message: `User context updated for restaurant: ${username}`,
      level: "info",
      data: {
        restaurantId: userId,
        email: email,
        name: username,
      },
    });
  }
};

